import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters } from '../components/GlobalFilterContext';
import { API_BASE } from '../../config';
import {
  ActiveShortageItem,
  ActiveShortagesResponse,
  PredictionItem,
  PredictionsResponse,
  ShortageTab,
} from '../types/shortage';
import {
  ShortageCausesResponse,
  ShortageAlternativesResponse,
} from '../types/shortageWarning';

// ============================================================
// Shortage Early Warning — Combined B4.4 + B4.5
// B4.4: Active Shortage Cards + Risk Radar (Issue #85)
// B4.5: Cause Analysis Feed + Alternative Tracker (Issue #86)
// ============================================================

// Extended tab type to include both B4.4 and B4.5 tabs
type CombinedTab = ShortageTab | 'cause-analysis' | 'alternatives';

// Cause category icons
const CAUSE_ICONS: Record<string, string> = {
  'Manufacturing disruption': '\u{1F3ED}',
  'Raw material shortage': '\u{1F9EA}',
  'Regulatory action': '\u{1F6D1}',
  'Demand spike': '\u{1F4C8}',
  'Quality issue': '\u{26A0}\u{FE0F}',
  'Distribution issue': '\u{1F69A}',
};

const CAUSE_COLORS: Record<string, string> = {
  'Manufacturing disruption': '#DC2626',
  'Raw material shortage': '#EA580C',
  'Regulatory action': '#7C3AED',
  'Demand spike': '#2563EB',
  'Quality issue': '#D97706',
  'Distribution issue': '#0891B2',
};

interface ShortageEarlyWarningPageProps {
  onSelectProduct: (id: number) => void;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  RiskBadge: React.ComponentType<{ risk: string }>;
  IconSpinner: React.ComponentType;
  IconAlert: React.ComponentType;
  IconShield: React.ComponentType;
}

export default function ShortageEarlyWarningPage({
  onSelectProduct,
  SectionHeader,
  ScoreRing,
  StatCard,
  RiskBadge,
  IconSpinner,
  IconAlert,
  IconShield,
}: ShortageEarlyWarningPageProps) {
  const { T } = useTheme();
  const { filters: globalFilters } = useGlobalFilters();

  // ── B4.4 State ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<CombinedTab>('active-shortages');
  const [shortagesData, setShortagesData] = useState<ActiveShortagesResponse | null>(null);
  const [shortagesLoading, setShortagesLoading] = useState(false);
  const [shortagesError, setShortagesError] = useState<string | null>(null);
  const [predictionsData, setPredictionsData] = useState<PredictionsResponse | null>(null);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionsError, setPredictionsError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'severity' | 'recency'>('severity');
  const [selectedHorizon, setSelectedHorizon] = useState<'30d' | '60d' | '90d'>('30d');

  // ── B4.5 State ─────────────────────────────────────────────
  const [causesData, setCausesData] = useState<ShortageCausesResponse | null>(null);
  const [causesLoading, setCausesLoading] = useState(false);
  const [causesError, setCausesError] = useState<string | null>(null);
  const [causeFilter, setCauseFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('Current');

  // Alternative recommendations state
  const [altShortages, setAltShortages] = useState<ActiveShortagesResponse | null>(null);
  const [altShortagesLoading, setAltShortagesLoading] = useState(false);
  const [altShortagesError, setAltShortagesError] = useState<string | null>(null);
  const [selectedShortageProductId, setSelectedShortageProductId] = useState<number | null>(null);
  const [alternativesData, setAlternativesData] = useState<ShortageAlternativesResponse | null>(null);
  const [alternativesLoading, setAlternativesLoading] = useState(false);
  const [alternativesError, setAlternativesError] = useState<string | null>(null);
  const [therapeuticFilter, setTherapeuticFilter] = useState<string>('all');

  // Export state (kept for potential future use)
  const [_exportLoading] = useState<'pdf' | 'csv' | null>(null);
  void _exportLoading;

  // Active detail section for the 5 tiles
  const [activeDetailSection, setActiveDetailSection] = useState<'countries' | 'cause' | 'risk' | null>(null);
  const [countryFilter, setCountryFilter] = useState<string | null>(null);

  // Shortage type filter: Limited, Unavailable, All
  const [shortageTypeFilter, setShortageTypeFilter] = useState<'All' | 'Limited' | 'Unavailable'>('All');
  // Search for product table
  const [productSearch, setProductSearch] = useState('');
  // Table pagination
  const [tablePage, setTablePage] = useState(1);
  const tablePageSize = 50;

  // Responsive
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  // ── B4.4: Load active shortages ────────────────────────────
  const loadShortages = useCallback(async () => {
    setShortagesLoading(true);
    setShortagesError(null);
    try {
      const params = new URLSearchParams({ page: '1', page_size: '5000' });
      if (severityFilter) params.set('severity', severityFilter);
      // Apply global shortage status filter
      if (globalFilters.shortageStatus) params.set('shortage_status', globalFilters.shortageStatus);
      // Apply global country filter
      if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
      const response = await fetch(`${API_BASE}/metasupply/shortages/active?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to load shortages (${response.status})`);
      const data: ActiveShortagesResponse = await response.json();
      setShortagesData(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load shortage data';
      setShortagesError(message);
    } finally {
      setShortagesLoading(false);
    }
  }, [severityFilter, globalFilters.shortageStatus, globalFilters.country]);

  // ── B4.4: Load predictions ─────────────────────────────────
  const loadPredictions = useCallback(async () => {
    setPredictionsLoading(true);
    setPredictionsError(null);
    try {
      const response = await fetch(`${API_BASE}/metasupply/shortages/predictions?page=1&page_size=100&min_risk=20`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to load predictions (${response.status})`);
      const data: PredictionsResponse = await response.json();
      setPredictionsData(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load prediction data';
      setPredictionsError(message);
    } finally {
      setPredictionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShortages();
  }, [loadShortages]);

  useEffect(() => {
    if (activeTab === 'risk-radar') {
      loadPredictions();
    }
  }, [activeTab, loadPredictions]);

  // ── B4.5: Load cause analysis data ─────────────────────────
  const loadCausesData = useCallback(async () => {
    setCausesLoading(true);
    setCausesError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      params.append('limit', '5000');
      // Apply global filters
      if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
      if (globalFilters.shortageStatus) params.set('shortage_status', globalFilters.shortageStatus);
      const response = await fetch(
        `${API_BASE}/api/metasupply/shortages/causes?${params.toString()}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`Failed to load data (${response.status})`);
      const data: ShortageCausesResponse = await response.json();
      setCausesData(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load cause analysis data';
      setCausesError(message);
    } finally {
      setCausesLoading(false);
    }
  }, [statusFilter, globalFilters.country, globalFilters.shortageStatus]);

  useEffect(() => {
    if (activeTab === 'cause-analysis') {
      loadCausesData();
    }
  }, [activeTab, loadCausesData]);

  // ── B4.5: Load shortages for alternatives tab ──────────────
  const loadAltShortages = useCallback(async () => {
    setAltShortagesLoading(true);
    setAltShortagesError(null);
    try {
      const params = new URLSearchParams({ page_size: '100' });
      // Apply global filters
      if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
      if (globalFilters.shortageStatus) params.set('shortage_status', globalFilters.shortageStatus);
      const response = await fetch(
        `${API_BASE}/metasupply/shortages/active?${params}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`Failed to load shortages (${response.status})`);
      const data: ActiveShortagesResponse = await response.json();
      setAltShortages(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load active shortages';
      setAltShortagesError(message);
    } finally {
      setAltShortagesLoading(false);
    }
  }, [globalFilters.country, globalFilters.shortageStatus]);

  useEffect(() => {
    if (activeTab === 'alternatives') {
      loadAltShortages();
    }
  }, [activeTab, loadAltShortages]);

  // ── B4.5: Load alternatives for a product ──────────────────
  const loadAlternatives = async (productId: number) => {
    setSelectedShortageProductId(productId);
    setAlternativesLoading(true);
    setAlternativesError(null);
    setAlternativesData(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/metasupply/shortages/${productId}/alternatives?max_results=10`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`Failed to load alternatives (${response.status})`);
      const data: ShortageAlternativesResponse = await response.json();
      setAlternativesData(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load alternatives';
      setAlternativesError(message);
    } finally {
      setAlternativesLoading(false);
    }
  };

  // ── Helpers (B4.4) ─────────────────────────────────────────
  const toggleExpanded = (id: number) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getSeverityColor = (severity: string): { bg: string; text: string; border: string } => {
    switch (severity) {
      case 'Critical': return { bg: T.dangerBg, text: T.danger, border: '#FECACA' };
      case 'High': return { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' };
      case 'Medium': return { bg: T.warningBg, text: T.warning, border: '#FDE68A' };
      case 'Low': return { bg: T.successBg, text: T.success, border: '#BBF7D0' };
      default: return { bg: '#F1F5F9', text: T.textMid, border: T.border };
    }
  };

  const getSeverityOrder = (severity: string): number => {
    switch (severity) {
      case 'Critical': return 1;
      case 'High': return 2;
      case 'Medium': return 3;
      case 'Low': return 4;
      default: return 5;
    }
  };

  const getRiskScoreForHorizon = (p: PredictionItem): number => {
    switch (selectedHorizon) {
      case '30d': return p.risk_score_30d;
      case '60d': return p.risk_score_60d;
      case '90d': return p.risk_score_90d;
    }
  };

  const getRiskLevelColor = (level: string): string => {
    switch (level) {
      case 'Critical': return T.danger;
      case 'High': return '#EA580C';
      case 'Medium': return T.warning;
      case 'Low': return T.success;
      default: return T.textLight;
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const sortedShortages = (): ActiveShortageItem[] => {
    if (!shortagesData) return [];
    const items = [...shortagesData.shortages];
    if (sortBy === 'severity') {
      items.sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
    } else {
      items.sort((a, b) => {
        const dateA = a.reported_date ? new Date(a.reported_date).getTime() : 0;
        const dateB = b.reported_date ? new Date(b.reported_date).getTime() : 0;
        return dateB - dateA;
      });
    }
    return items;
  };

  // ── Helpers (B4.5) ─────────────────────────────────────────
  const getTrendIcon = (trend: string): string => {
    switch (trend) {
      case 'increasing': return '\u2191';
      case 'decreasing': return '\u2193';
      default: return '\u2192';
    }
  };

  const getTrendColor = (trend: string): string => {
    switch (trend) {
      case 'increasing': return T.danger;
      case 'decreasing': return T.success;
      default: return T.warning;
    }
  };

  const getTrendLabel = (trend: string): string => {
    switch (trend) {
      case 'increasing': return 'Increasing';
      case 'decreasing': return 'Decreasing';
      default: return 'Stable';
    }
  };

  const formatPrice = (price: number | null): string => {
    if (price === null || price === undefined) return 'N/A';
    return `$${price.toFixed(2)}`;
  };

  // Unique therapeutic categories from active shortages (for alternatives tab)
  const therapeuticCategories: string[] = altShortages
    ? Array.from(new Set(
        altShortages.shortages
          .map(s => s.therapeutic_category)
          .filter((c): c is string => !!c)
      )).sort()
    : [];

  // Filtered shortages for the alternatives tab
  const filteredAltShortages = altShortages?.shortages.filter(s => {
    if (therapeuticFilter === 'all') return true;
    return s.therapeutic_category === therapeuticFilter;
  }) || [];

  // Filtered causes
  const filteredCauses = causesData?.causes.filter(c => {
    if (causeFilter === 'all') return true;
    return c.category === causeFilter;
  }) || [];

  // Available cause categories for filter
  const causeCategories = causesData?.causes.map(c => c.category) || [];

  // Export handler kept but unused (export buttons removed per Tony's request)
  void API_BASE; // used by export handler if re-enabled
  const _handleExport = async (format: 'pdf' | 'csv') => {
    void format;
  };  void _handleExport;

  // ── Tab definitions (all 4 tabs) ───────────────────────────
  const tabs: { id: CombinedTab; label: string; icon: React.ReactNode }[] = [
    { id: 'active-shortages', label: 'Active Shortages', icon: <IconAlert /> },
    { id: 'risk-radar', label: 'Risk Radar', icon: <IconShield /> },
    { id: 'cause-analysis', label: 'Cause Analysis', icon: <span>{'\u{1F50D}'}</span> },
    { id: 'alternatives', label: 'Alternative Tracker', icon: <span>{'\u{1F504}'}</span> },
  ];

  // ============================================================
  // RENDER: Hero Section
  // ============================================================
  const renderHero = () => (
    <div style={{
      position: 'relative',
      background: T.offWhite, border: `1px solid ${T.border}`,
      borderRadius: T.radius,
      padding: isMobile ? '28px 20px' : '40px 44px',
      overflow: 'hidden',
      marginBottom: 24,
    }}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        }}>
          <IconAlert />
          <span style={{
            fontSize: 11, fontWeight: 700, color: T.red,
            textTransform: 'uppercase', letterSpacing: '0.15em',
            fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
          }}>
            Shortage Early Warning System
          </span>
        </div>
        <h1 style={{
          fontSize: isMobile ? 24 : 32, fontWeight: 800,
          color: T.textDark, letterSpacing: '-0.02em',
          lineHeight: 1.2, marginBottom: 8,
          fontFamily: "'Playfair Display', Georgia, serif",
        }}>
          Drug Shortage Intelligence
        </h1>
        <p style={{
          fontSize: 14, color: T.textMid,
          maxWidth: 600, lineHeight: 1.6,
        }}>
          Monitor active drug shortages, track severity trends, view predictive
          risk assessments, analyze root causes, and discover AI-recommended alternatives.
        </p>
      </div>
    </div>
  );

  // ============================================================
  // Compute deduplicated shortages (unique by NDC, current only)
  // ============================================================
  const getDeduplicatedShortages = (): ActiveShortageItem[] => {
    if (!shortagesData) return [];
    const seen = new Set<string>();
    const deduped: ActiveShortageItem[] = [];
    // Sort by severity first so we keep the most critical record per NDC
    const sorted = [...shortagesData.shortages]
      .filter(s => s.shortage_status === 'Current')
      .sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
    for (const s of sorted) {
      const key = s.ndc_code || `id-${s.shortage_id}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(s);
      }
    }
    return deduped;
  };

  const dedupedShortages = shortagesData ? getDeduplicatedShortages() : [];

  // Compute unique manufacturers from deduplicated shortages
  const uniqueManufacturers = new Set(dedupedShortages.map(s => s.manufacturer).filter(Boolean));

  // Country data: prefer backend shortage_countries (accurate across all shortages),
  // fall back to client-side computation from paginated data
  const backendCountries = shortagesData?.shortage_countries ?? [];

  // ============================================================
  // RENDER: 5 Navigation Tiles
  // ============================================================
  const renderSummaryTiles = () => {
    /* SectionHeader kept in props for consistency with other pages */
    void SectionHeader;

    const tiles = [
      { id: null as string | null, title: 'Total Drugs', value: (shortagesData?.shortage_product_count ?? dedupedShortages.length).toLocaleString(), color: T.navy },
      { id: null as string | null, title: 'Manufacturers', value: uniqueManufacturers.size.toLocaleString(), color: '#EA580C' },
      { id: 'countries' as string | null, title: 'Countries', value: '', color: T.warning, isList: true as boolean },
      { id: 'cause' as string | null, title: 'Cause Analysis', value: causesData ? causesData.causes.length.toLocaleString() : '—', color: '#7C3AED' },
      { id: 'risk' as string | null, title: 'Risk Analysis', value: predictionsData ? predictionsData.predictions.length.toLocaleString() : '—', color: T.navyLight },
    ];

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
        gap: 16,
        marginBottom: 24,
      }}>
        {tiles.map((tile, idx) => {
          const isClickable = tile.id !== null;
          const isActive = tile.id !== null && activeDetailSection === tile.id;
          const isList = (tile as { isList?: boolean }).isList;

          // Special "Top Shortage Countries" tile — shows a mini-list
          if (isList) {
            const top5 = backendCountries.slice(0, 5);
            return (
              <div
                key={idx}
                onClick={() => {
                  setActiveDetailSection(isActive ? null : 'countries');
                }}
                style={{
                  padding: '12px 16px',
                  background: isActive ? tile.color : T.white,
                  border: `1.5px solid ${isActive ? tile.color : T.border}`,
                  borderRadius: T.radiusSm,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: isActive ? `0 2px 8px ${tile.color}30` : T.shadow,
                }}
              >
                <div style={{
                  fontSize: 10, fontWeight: 700, color: isActive ? 'rgba(255,255,255,0.85)' : T.textLight,
                  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
                }}>
                  Top Shortage Countries
                </div>
                {top5.length === 0 ? (
                  <div style={{ fontSize: 12, color: isActive ? 'rgba(255,255,255,0.7)' : T.textLight }}>No data</div>
                ) : (
                  top5.map((c, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: 11, lineHeight: 1.6,
                      color: isActive ? T.white : T.textDark,
                    }}>
                      <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                        {c.country}
                      </span>
                      <span style={{ fontWeight: 800, color: isActive ? T.white : tile.color, fontSize: 12 }}>
                        {c.product_count.toLocaleString()}
                      </span>
                    </div>
                  ))
                )}
              </div>
            );
          }

          return (
            <div
              key={idx}
              onClick={() => {
                if (isClickable) {
                  setActiveDetailSection(isActive ? null : tile.id as 'countries' | 'cause' | 'risk');
                  if (tile.id === 'cause' && !causesData) loadCausesData();
                  if (tile.id === 'risk' && !predictionsData) loadPredictions();
                }
              }}
              style={{
                padding: '18px 16px',
                background: isActive ? tile.color : T.white,
                border: `1.5px solid ${isActive ? tile.color : T.border}`,
                borderRadius: T.radiusSm,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.15s',
                boxShadow: isActive ? `0 2px 8px ${tile.color}30` : T.shadow,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 800, color: isActive ? T.white : tile.color, lineHeight: 1 }}>
                {tile.value}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? 'rgba(255,255,255,0.9)' : T.textDark, marginTop: 6 }}>
                {tile.title}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ============================================================
  // RENDER: Countries Detail Section
  // ============================================================
  const renderCountriesDetail = () => {
    // Use backend shortage_countries (accurate across all shortages, uses product COO)
    const countryList = backendCountries;

    return (
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, padding: isMobile ? 16 : 24,
        boxShadow: T.shadow, marginBottom: 24,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 16 }}>Shortage Countries</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <button
            onClick={() => setCountryFilter(null)}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${!countryFilter ? T.navy : T.border}`,
              background: !countryFilter ? T.navy : T.white,
              color: !countryFilter ? T.white : T.textMid,
              fontFamily: 'inherit', transition: 'all 0.15s',
            }}
          >All</button>
          {countryList.map(({ country, product_count }) => (
            <button
              key={country}
              onClick={() => setCountryFilter(countryFilter === country ? null : country)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${countryFilter === country ? T.navy : T.border}`,
                background: countryFilter === country ? T.navy : T.white,
                color: countryFilter === country ? T.white : T.textMid,
                fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >{country} ({product_count.toLocaleString()})</button>
          ))}
        </div>
      </div>
    );
  };

  // Tab navigation removed — replaced by 5 tiles above
  void tabs;

  // ============================================================
  // RENDER: Shortage Card (individual — B4.4)
  // ============================================================
  const renderShortageCard = (shortage: ActiveShortageItem) => {
    const expanded = expandedCards.has(shortage.shortage_id);
    const sevColor = getSeverityColor(shortage.severity);

    return (
      <div
        key={shortage.shortage_id}
        style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          overflow: 'hidden',
          boxShadow: T.shadow,
          transition: 'box-shadow 0.2s',
          borderLeft: `4px solid ${sevColor.text}`,
        }}
      >
        {/* Card header */}
        <div
          onClick={() => toggleExpanded(shortage.shortage_id)}
          style={{
            padding: isMobile ? '16px 14px' : '20px 24px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? 12 : 16,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <RiskBadge risk={shortage.severity.toLowerCase()} />
              <span style={{
                background: shortage.shortage_status === 'Current' ? T.dangerBg : T.warningBg,
                color: shortage.shortage_status === 'Current' ? T.danger : T.warning,
                borderRadius: 20, padding: '4px 10px', fontSize: 10, fontWeight: 600,
              }}>
                {shortage.shortage_status}
              </span>
              {shortage.alternatives_available && (
                <span style={{
                  background: T.successBg, color: T.success,
                  borderRadius: 20, padding: '4px 10px', fontSize: 10, fontWeight: 600,
                }}>
                  Alternatives Available
                </span>
              )}
            </div>
            <h3 style={{
              fontSize: 16, fontWeight: 700, color: T.navy,
              marginBottom: 4, lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {shortage.product_name || shortage.ndc_code || 'Unknown Product'}
            </h3>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: T.textMid }}>
              {shortage.manufacturer && <span>{shortage.manufacturer}</span>}
              {shortage.ndc_code && <span style={{ fontFamily: 'monospace', fontSize: 11 }}>NDC: {shortage.ndc_code}</span>}
              {shortage.days_active != null && (
                <span style={{ color: shortage.days_active > 90 ? T.danger : T.textMid }}>
                  {shortage.days_active} days active
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2"
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        </div>

        {/* Expanded detail section */}
        {expanded && (
          <div style={{
            borderTop: `1px solid ${T.border}`,
            padding: isMobile ? '16px 14px' : '20px 24px',
            background: T.offWhite,
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr 1fr',
              gap: 16, marginBottom: 16,
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                  Cause / Reason
                </div>
                <div style={{ fontSize: 13, color: T.textDark, lineHeight: 1.5 }}>
                  {shortage.reason || 'No cause specified by FDA'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                  Timeline
                </div>
                <div style={{ fontSize: 13, color: T.textDark }}>
                  <div>Reported: {formatDate(shortage.reported_date)}</div>
                  {shortage.resolved_date && <div>Resolved: {formatDate(shortage.resolved_date)}</div>}
                  {shortage.days_active != null && (
                    <div style={{ marginTop: 4 }}>
                      <span style={{
                        fontWeight: 700,
                        color: shortage.days_active > 180 ? T.danger : shortage.days_active > 90 ? T.warning : T.textDark,
                      }}>
                        {shortage.days_active} days
                      </span> since first reported
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                  Product Details
                </div>
                <div style={{ fontSize: 13, color: T.textDark }}>
                  {shortage.generic_name && <div>Generic: {shortage.generic_name}</div>}
                  {shortage.therapeutic_category && <div>Category: {shortage.therapeutic_category}</div>}
                  {shortage.api_source_country && <div>API Source: {shortage.api_source_country}</div>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {shortage.product_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSelectProduct(shortage.product_id as number); }}
                  style={{
                    padding: '8px 20px', borderRadius: T.radiusSm,
                    border: `1px solid ${T.gold}`, background: `${T.gold}10`,
                    color: T.navy, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                  }}
                >
                  View Product Detail
                </button>
              )}
              {shortage.product_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); loadAlternatives(shortage.product_id as number); }}
                  style={{
                    padding: '8px 20px', borderRadius: T.radiusSm,
                    border: `1px solid ${T.navy}`, background: `${T.navy}10`,
                    color: T.navy, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                  }}
                >
                  {alternativesLoading && selectedShortageProductId === shortage.product_id ? 'Loading...' : 'View Alternatives'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER: Active Shortages Tab (B4.4)
  // ============================================================
  const renderActiveShortages = () => {
    if (shortagesLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 16 }}>
          <IconSpinner />
          <span style={{ fontSize: 14, color: T.textMid }}>Loading active shortages...</span>
        </div>
      );
    }
    if (shortagesError) {
      return (
        <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 32, textAlign: 'center' }}>
          <IconAlert />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.danger, marginTop: 12 }}>Failed to Load Data</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>{shortagesError}</p>
          <button onClick={loadShortages} style={{ marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
        </div>
      );
    }
    if (!shortagesData || shortagesData.shortages.length === 0) {
      return (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 60, textAlign: 'center' }}>
          <IconShield />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginTop: 12 }}>No Active Shortages</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>No drug shortages matching your filters are currently active.</p>
        </div>
      );
    }

    const items = sortedShortages();

    return (
      <div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[null, 'Critical', 'High', 'Medium', 'Low'].map(sev => {
              const active = severityFilter === sev;
              return (
                <button key={sev || 'all'} onClick={() => setSeverityFilter(sev)}
                  style={{
                    padding: '6px 14px', borderRadius: 20,
                    border: `1px solid ${active ? T.gold : T.border}`,
                    background: active ? `${T.gold}15` : T.white,
                    color: active ? T.navy : T.textMid,
                    fontSize: 12, fontWeight: active ? 700 : 500,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                >
                  {sev || 'All'}
                </button>
              );
            })}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            <button onClick={() => setSortBy('severity')} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontFamily: 'inherit', border: `1px solid ${sortBy === 'severity' ? T.navy : T.border}`, background: sortBy === 'severity' ? T.navy : T.white, color: sortBy === 'severity' ? T.white : T.textMid, fontWeight: sortBy === 'severity' ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>By Severity</button>
            <button onClick={() => setSortBy('recency')} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontFamily: 'inherit', border: `1px solid ${sortBy === 'recency' ? T.navy : T.border}`, background: sortBy === 'recency' ? T.navy : T.white, color: sortBy === 'recency' ? T.white : T.textMid, fontWeight: sortBy === 'recency' ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>By Recency</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(shortage => renderShortageCard(shortage))}
        </div>
        <div style={{ marginTop: 16, padding: '12px 16px', background: T.offWhite, borderRadius: T.radiusSm, fontSize: 12, color: T.textMid, textAlign: 'center' }}>
          Showing {items.length} of {shortagesData.total} active shortages
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER: Risk Radar Visualization (B4.4)
  // ============================================================
  const renderRiskRadar = () => {
    if (predictionsLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 16 }}>
          <IconSpinner />
          <span style={{ fontSize: 14, color: T.textMid }}>Generating risk predictions...</span>
        </div>
      );
    }
    if (predictionsError) {
      return (
        <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 32, textAlign: 'center' }}>
          <IconAlert />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.danger, marginTop: 12 }}>Failed to Load Predictions</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>{predictionsError}</p>
          <button onClick={loadPredictions} style={{ marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
        </div>
      );
    }
    if (!predictionsData || predictionsData.predictions.length === 0) {
      return (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 60, textAlign: 'center' }}>
          <IconShield />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginTop: 12 }}>No Risk Predictions Available</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>Insufficient data to generate shortage risk predictions.</p>
        </div>
      );
    }

    // Recompute risk_level per product based on selected horizon score
    // (backend only computes risk_level from the 30-day score)
    const getRiskLevelForScore = (score: number): string => {
      if (score >= 70) return 'Critical';
      if (score >= 45) return 'High';
      if (score >= 20) return 'Medium';
      return 'Low';
    };

    const predictionsWithHorizon = predictionsData.predictions.map(p => {
      const horizonScore = getRiskScoreForHorizon(p);
      return { ...p, horizonScore, horizonLevel: getRiskLevelForScore(horizonScore) };
    });
    // Re-sort by the selected horizon score (descending)
    predictionsWithHorizon.sort((a, b) => b.horizonScore - a.horizonScore);

    const critical = predictionsWithHorizon.filter(p => p.horizonLevel === 'Critical');
    const high = predictionsWithHorizon.filter(p => p.horizonLevel === 'High');
    const medium = predictionsWithHorizon.filter(p => p.horizonLevel === 'Medium');
    const low = predictionsWithHorizon.filter(p => p.horizonLevel === 'Low');

    const radarSize = isMobile ? 280 : 400;
    const cx = radarSize / 2;
    const cy = radarSize / 2;
    const maxRadius = radarSize / 2 - 30;

    const scoreToRadius = (score: number): number => {
      return maxRadius * (1 - score / 100) * 0.85 + maxRadius * 0.1;
    };

    const plotItems = predictionsWithHorizon.slice(0, 40).map((p, idx) => {
      const score = p.horizonScore;
      const r = scoreToRadius(score);
      const angle = (idx / Math.min(predictionsWithHorizon.length, 40)) * 2 * Math.PI - Math.PI / 2;
      const jitter = ((idx * 137.508) % 1) * 0.4 - 0.2;
      const finalAngle = angle + jitter;
      return { ...p, x: cx + r * Math.cos(finalAngle), y: cy + r * Math.sin(finalAngle), score };
    });

    return (
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginRight: 8 }}>Prediction Horizon:</span>
          {(['30d', '60d', '90d'] as const).map(h => {
            const active = selectedHorizon === h;
            return (
              <button key={h} onClick={() => setSelectedHorizon(h)} style={{ padding: '8px 20px', borderRadius: 20, fontSize: 13, fontFamily: 'inherit', border: `1px solid ${active ? T.gold : T.border}`, background: active ? `${T.gold}15` : T.white, color: active ? T.navy : T.textMid, fontWeight: active ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>
                {h === '30d' ? '30 Days' : h === '60d' ? '60 Days' : '90 Days'}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          <StatCard title="Critical Risk" value={critical.length} subtitle="Immediate action needed" color={T.danger} accent="#000000" />
          <StatCard title="High Risk" value={high.length} subtitle="Close monitoring" color="#EA580C" accent="#000000" />
          <StatCard title="Medium Risk" value={medium.length} subtitle="Watch list" color={T.warning} accent="#000000" />
          <StatCard title="Low Risk" value={low.length} subtitle="Stable supply" color={T.success} accent="#000000" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24 }}>
          {/* Radar chart */}
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16, alignSelf: 'flex-start' }}>
              Risk Radar &#8212; {selectedHorizon === '30d' ? '30-Day' : selectedHorizon === '60d' ? '60-Day' : '90-Day'} Outlook
            </div>
            <svg width={radarSize} height={radarSize} viewBox={`0 0 ${radarSize} ${radarSize}`}>
              {[25, 50, 75, 100].map(pct => {
                const r = maxRadius * (pct / 100);
                return <circle key={pct} cx={cx} cy={cy} r={r} fill="none" stroke={T.border} strokeWidth="1" strokeDasharray={pct === 100 ? 'none' : '4 4'} opacity={0.5} />;
              })}
              <text x={cx + 4} y={cy - maxRadius * 0.1 + 4} fontSize="9" fill={T.danger} fontWeight="700" opacity={0.7}>CRITICAL</text>
              <text x={cx + 4} y={cy - maxRadius * 0.35 + 4} fontSize="9" fill="#EA580C" fontWeight="700" opacity={0.7}>HIGH</text>
              <text x={cx + 4} y={cy - maxRadius * 0.6 + 4} fontSize="9" fill={T.warning} fontWeight="700" opacity={0.6}>MEDIUM</text>
              <text x={cx + 4} y={cy - maxRadius * 0.85 + 4} fontSize="9" fill={T.success} fontWeight="700" opacity={0.5}>LOW</text>
              <line x1={cx} y1={10} x2={cx} y2={radarSize - 10} stroke={T.border} strokeWidth="0.5" opacity={0.3} />
              <line x1={10} y1={cy} x2={radarSize - 10} y2={cy} stroke={T.border} strokeWidth="0.5" opacity={0.3} />
              {plotItems.map((item, idx) => {
                const dotColor = getRiskLevelColor(item.horizonLevel);
                const dotSize = item.score >= 75 ? 7 : item.score >= 50 ? 6 : 5;
                return (
                  <g key={idx}>
                    <circle cx={item.x} cy={item.y} r={dotSize} fill={dotColor} opacity={0.8}
                      style={{ cursor: 'pointer', transition: 'r 0.2s' }}
                      onClick={() => onSelectProduct(item.product_id)}>
                      <title>{`${item.product_name || item.ndc_code}\nRisk: ${item.score}% (${item.horizonLevel})`}</title>
                    </circle>
                    {item.horizonLevel === 'Critical' && (
                      <circle cx={item.x} cy={item.y} r={dotSize + 3} fill="none" stroke={T.danger} strokeWidth="1" opacity={0.3} />
                    )}
                  </g>
                );
              })}
            </svg>
            <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[{ label: 'Critical', color: T.danger }, { label: 'High', color: '#EA580C' }, { label: 'Medium', color: T.warning }, { label: 'Low', color: T.success }].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color }} />
                  <span style={{ fontSize: 11, color: T.textMid }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Product list */}
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow, maxHeight: radarSize + 80, overflowY: 'auto' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
              Products by Risk Level ({predictionsWithHorizon.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {predictionsWithHorizon.slice(0, 20).map((p, idx) => {
                const riskColor = getRiskLevelColor(p.horizonLevel);
                const score = p.horizonScore;
                return (
                  <div key={idx} onClick={() => onSelectProduct(p.product_id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: T.radiusSm, border: `1px solid ${T.borderLight}`, cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.offWhite; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <ScoreRing score={score} size={36} strokeWidth={3} color={riskColor} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.product_name || p.ndc_code}</div>
                      <div style={{ fontSize: 11, color: T.textMid }}>{p.manufacturer || 'Unknown'}</div>
                    </div>
                    <span style={{ background: `${riskColor}15`, color: riskColor, borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{p.horizonLevel}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 60, flexShrink: 0 }}>
                      {[{ label: '30d', val: p.risk_score_30d }, { label: '60d', val: p.risk_score_60d }, { label: '90d', val: p.risk_score_90d }].map(bar => (
                        <div key={bar.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 8, color: T.textLight, width: 18 }}>{bar.label}</span>
                          <div style={{ flex: 1, height: 4, background: T.borderLight, borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${bar.val}%`, height: '100%', background: bar.val >= 75 ? T.danger : bar.val >= 50 ? '#EA580C' : bar.val >= 25 ? T.warning : T.success, borderRadius: 2, transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {predictionsWithHorizon.length > 20 && (
              <div style={{ marginTop: 12, fontSize: 12, color: T.textMid, textAlign: 'center', padding: '8px 0' }}>
                Showing top 20 of {predictionsWithHorizon.length} at-risk products
              </div>
            )}
          </div>
        </div>
        {predictionsData.generated_at && (
          <div style={{ marginTop: 16, fontSize: 11, color: T.textLight, textAlign: 'right' }}>
            Predictions generated: {new Date(predictionsData.generated_at).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER: Cause Analysis Summary Stats (B4.5)
  // ============================================================
  const renderCauseSummaryStats = () => {
    if (!causesData) return null;
    const totalCauses = causesData.causes.length;
    const increasingCount = causesData.causes.filter(c => c.trend === 'increasing').length;
    const topCause = causesData.causes.length > 0 ? causesData.causes[0] : null;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Total Shortages" value={causesData.total_shortages.toLocaleString()} subtitle={statusFilter !== 'all' ? `Status: ${statusFilter}` : 'All statuses'} color={T.danger} accent="#000000" />
        <StatCard title="Cause Categories" value={totalCauses.toLocaleString()} subtitle="Identified categories" color={T.navy} accent="#000000" />
        <StatCard title="Increasing Trends" value={increasingCount.toLocaleString()} subtitle="Categories trending up" color={increasingCount > 0 ? T.danger : T.success} accent="#000000" />
        <StatCard title="Top Cause" value={topCause ? topCause.count.toLocaleString() : '0'} subtitle={topCause ? topCause.category : 'N/A'} color={T.warning} accent="#000000" />
      </div>
    );
  };

  // ============================================================
  // RENDER: Cause Filter Bar (B4.5)
  // ============================================================
  const renderCauseFilters = () => (
    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Status:</span>
        {['Current', 'Anticipated', 'Resolved', 'all'].map(status => (
          <button key={status} onClick={() => setStatusFilter(status)}
            style={{
              padding: '6px 14px', borderRadius: 20,
              border: `1px solid ${statusFilter === status ? T.gold : T.border}`,
              background: statusFilter === status ? `${T.gold}12` : T.white,
              color: statusFilter === status ? T.navy : T.textMid,
              cursor: 'pointer', fontSize: 12, fontWeight: statusFilter === status ? 700 : 500,
              fontFamily: 'inherit', transition: 'all 0.2s',
            }}
          >
            {status === 'all' ? 'All' : status}
          </button>
        ))}
      </div>
      {causeCategories.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Cause:</span>
          <select value={causeFilter} onChange={e => setCauseFilter(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textDark, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}>
            <option value="all">All Categories</option>
            {causeCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
      )}
    </div>
  );

  // ============================================================
  // RENDER: Cause Analysis Feed (B4.5)
  // ============================================================
  const renderCauseAnalysisFeed = () => {
    if (causesLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 16 }}>
          <IconSpinner />
          <span style={{ fontSize: 14, color: T.textMid }}>Loading cause analysis...</span>
        </div>
      );
    }
    if (causesError) {
      return (
        <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 32, textAlign: 'center' }}>
          <IconAlert />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.danger, marginTop: 12 }}>Failed to Load Data</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>{causesError}</p>
          <button onClick={loadCausesData} style={{ marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
        </div>
      );
    }
    if (!causesData || filteredCauses.length === 0) {
      return (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 60, textAlign: 'center' }}>
          <IconAlert />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginTop: 12 }}>No Causes Found</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>No shortage causes match the current filters.</p>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {filteredCauses.map((cause) => (
          <div key={cause.category} style={{
            background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
            padding: isMobile ? 16 : 24, boxShadow: T.shadow,
            borderLeft: `4px solid ${CAUSE_COLORS[cause.category] || T.textLight}`,
            transition: 'box-shadow 0.2s',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 28, lineHeight: 1 }}>{CAUSE_ICONS[cause.category] || '\u{2753}'}</span>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: T.navy, letterSpacing: '-0.01em' }}>{cause.category}</h3>
                  <span style={{ fontSize: 12, color: T.textMid }}>{cause.count} shortage{cause.count !== 1 ? 's' : ''} ({cause.percentage}% of total)</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, background: `${getTrendColor(cause.trend)}10`, border: `1px solid ${getTrendColor(cause.trend)}30` }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: getTrendColor(cause.trend) }}>{getTrendIcon(cause.trend)}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: getTrendColor(cause.trend) }}>{getTrendLabel(cause.trend)}</span>
              </div>
            </div>
            {/* Severity breakdown badges */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {Object.entries(cause.severity_breakdown).map(([severity, count]) => {
                const sColor = getSeverityColor(severity);
                return (
                  <span key={severity} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 12px', borderRadius: 20,
                    background: sColor.bg, color: sColor.text, border: `1px solid ${sColor.border}`,
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    {severity}: {count}
                  </span>
                );
              })}
            </div>
            {/* Percentage bar */}
            <div style={{ width: '100%', height: 6, borderRadius: 3, background: T.border, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ width: `${Math.min(cause.percentage, 100)}%`, height: '100%', borderRadius: 3, background: CAUSE_COLORS[cause.category] || T.textLight, transition: 'width 0.6s ease' }} />
            </div>
            {/* Affected products */}
            {cause.example_products.length > 0 && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, display: 'block' }}>Affected Products</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {cause.example_products.map(p => (
                    <button key={p.product_id} onClick={() => onSelectProduct(p.product_id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 12px', borderRadius: T.radiusSm,
                        border: `1px solid ${T.border}`, background: T.offWhite,
                        color: T.navy, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                      }}>
                      <span style={{ color: T.textLight, fontSize: 10 }}>NDC {p.ndc_code}</span>
                      {p.product_name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ============================================================
  // RENDER: Alternatives Tab (B4.5)
  // ============================================================
  const renderAlternativesTab = () => {
    if (altShortagesLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 16 }}>
          <IconSpinner />
          <span style={{ fontSize: 14, color: T.textMid }}>Loading active shortages...</span>
        </div>
      );
    }
    if (altShortagesError) {
      return (
        <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 32, textAlign: 'center' }}>
          <IconAlert />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.danger, marginTop: 12 }}>Failed to Load Shortages</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>{altShortagesError}</p>
          <button onClick={loadAltShortages} style={{ marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
        </div>
      );
    }
    if (!altShortages || altShortages.shortages.length === 0) {
      return (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 60, textAlign: 'center' }}>
          <IconAlert />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginTop: 12 }}>No Active Shortages</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>There are currently no active shortages in the system.</p>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Alt-tab summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 16 }}>
          <StatCard title="Total Active" value={altShortages.total.toLocaleString()} subtitle="Current + Anticipated" color={T.danger} accent="#000000" />
          <StatCard title="Critical" value={(altShortages.severity_summary['Critical'] || 0).toLocaleString()} subtitle="Requires immediate action" color={T.danger} accent="#000000" />
          <StatCard title="High" value={(altShortages.severity_summary['High'] || 0).toLocaleString()} subtitle="High priority" color="#EA580C" accent="#000000" />
          <StatCard title="Medium/Low" value={((altShortages.severity_summary['Medium'] || 0) + (altShortages.severity_summary['Low'] || 0)).toLocaleString()} subtitle="Monitor closely" color={T.warning} accent="#000000" />
        </div>
        {/* Therapeutic class filter */}
        {therapeuticCategories.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Therapeutic Class:</span>
            <select value={therapeuticFilter} onChange={e => setTherapeuticFilter(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textDark, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}>
              <option value="all">All Classes ({altShortages.shortages.length})</option>
              {therapeuticCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        )}
        {/* Shortage list table */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: 'hidden', boxShadow: T.shadow }}>
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 100px 120px 140px', gap: 12, padding: '12px 20px', background: T.offWhite, borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <span>Product</span><span>Manufacturer</span><span>Severity</span><span>Reported</span><span>Alternatives</span>
            </div>
          )}
          {filteredAltShortages.map((shortage, idx) => (
            <div key={shortage.shortage_id} style={{
              display: isMobile ? 'flex' : 'grid',
              flexDirection: isMobile ? 'column' : undefined,
              gridTemplateColumns: isMobile ? undefined : '1fr 150px 100px 120px 140px',
              gap: isMobile ? 8 : 12,
              padding: isMobile ? '16px' : '16px 20px',
              borderBottom: idx < filteredAltShortages.length - 1 ? `1px solid ${T.borderLight}` : 'none',
              alignItems: isMobile ? undefined : 'center',
              transition: 'background 0.15s',
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.navy, cursor: shortage.product_id ? 'pointer' : 'default' }}
                  onClick={() => { if (shortage.product_id) onSelectProduct(shortage.product_id); }}>
                  {shortage.product_name || 'Unknown Product'}
                </div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                  {shortage.generic_name && <span>{shortage.generic_name} &middot; </span>}NDC {shortage.ndc_code || 'N/A'}
                </div>
                {shortage.therapeutic_category && <div style={{ fontSize: 10, color: T.textMid, marginTop: 2 }}>{shortage.therapeutic_category}</div>}
              </div>
              <div style={{ fontSize: 12, color: T.textMid }}>
                {isMobile && <span style={{ fontSize: 10, color: T.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Manufacturer: </span>}
                {shortage.manufacturer || 'Unknown'}
              </div>
              <div>
                {(() => {
                  const sev = getSeverityColor(shortage.severity || 'Medium');
                  return <span style={{ display: 'inline-flex', padding: '4px 10px', borderRadius: 20, background: sev.bg, color: sev.text, border: `1px solid ${sev.border}`, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{shortage.severity || 'Medium'}</span>;
                })()}
              </div>
              <div style={{ fontSize: 12, color: T.textMid }}>
                {isMobile && <span style={{ fontSize: 10, color: T.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reported: </span>}
                {formatDate(shortage.reported_date)}
                {shortage.days_active !== null && <div style={{ fontSize: 10, color: T.textLight }}>{shortage.days_active}d active</div>}
              </div>
              <div>
                {shortage.product_id ? (
                  <button onClick={() => loadAlternatives(shortage.product_id!)}
                    style={{
                      padding: '8px 16px', borderRadius: T.radiusSm,
                      border: `1px solid ${T.gold}`,
                      background: selectedShortageProductId === shortage.product_id ? `${T.gold}18` : T.white,
                      color: T.navy, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', whiteSpace: 'nowrap',
                    }}>
                    {alternativesLoading && selectedShortageProductId === shortage.product_id ? 'Loading...' : 'View Alternatives'}
                  </button>
                ) : <span style={{ fontSize: 11, color: T.textLight }}>No product linked</span>}
              </div>
            </div>
          ))}
        </div>
        {/* Alternatives panel */}
        {renderAlternativesPanel()}
      </div>
    );
  };

  // ============================================================
  // RENDER: Alternatives Panel (B4.5)
  // ============================================================
  const renderAlternativesPanel = () => {
    if (!selectedShortageProductId) return null;
    if (alternativesLoading) {
      return (
        <div style={{ background: T.white, border: `1px solid ${T.gold}40`, borderRadius: T.radius, padding: 40, textAlign: 'center', boxShadow: T.shadowMd }}>
          <IconSpinner />
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 12 }}>Finding AI-recommended alternatives...</p>
        </div>
      );
    }
    if (alternativesError) {
      return (
        <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 24, textAlign: 'center' }}>
          <IconAlert />
          <p style={{ fontSize: 13, color: T.danger, marginTop: 8 }}>{alternativesError}</p>
        </div>
      );
    }
    if (!alternativesData) return null;

    return (
      <div style={{ background: T.white, border: `1px solid ${T.gold}40`, borderRadius: T.radius, padding: isMobile ? 16 : 24, boxShadow: T.shadowMd }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.12em' }}>AI-Recommended Alternatives</span>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: T.navy, letterSpacing: '-0.01em' }}>{alternativesData.product_name}</h3>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>
              Status: {alternativesData.shortage_status}
              {alternativesData.shortage_reason && <span> &middot; Reason: {alternativesData.shortage_reason}</span>}
            </div>
          </div>
          <button onClick={() => { setSelectedShortageProductId(null); setAlternativesData(null); }}
            style={{ padding: '6px 14px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.offWhite, color: T.textMid, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
            Close
          </button>
        </div>
        {/* AI summary */}
        {alternativesData.ai_summary && (
          <div style={{ background: T.infoBg, border: '1px solid #BFDBFE', borderRadius: T.radiusSm, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 14 }}>{'\u{1F916}'}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.navyLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>AI Analysis</span>
            </div>
            <p style={{ fontSize: 13, color: T.textDark, lineHeight: 1.6 }}>{alternativesData.ai_summary}</p>
          </div>
        )}
        {/* Alternatives list */}
        {alternativesData.alternatives.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', background: T.offWhite, borderRadius: T.radiusSm }}>
            <p style={{ fontSize: 13, color: T.textMid }}>No therapeutic alternatives found for this product.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {alternativesData.alternatives.map((alt, idx) => (
              <div key={alt.product_id} style={{
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 12 : 20, padding: 16, borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`,
                background: idx === 0 ? `${T.gold}06` : T.white,
                alignItems: isMobile ? 'stretch' : 'center', transition: 'box-shadow 0.2s',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: idx === 0 ? T.gold : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : T.border,
                  color: idx < 3 ? T.white : T.textMid,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 800, flexShrink: 0,
                }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.navy, cursor: 'pointer' }} onClick={() => onSelectProduct(alt.product_id)}>{alt.product_name}</div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                    {alt.generic_name && <span>{alt.generic_name} &middot; </span>}NDC {alt.ndc_code}{alt.manufacturer && <span> &middot; {alt.manufacturer}</span>}
                  </div>
                  {alt.recommendation_reason && <div style={{ fontSize: 11, color: T.textMid, marginTop: 4, fontStyle: 'italic' }}>{alt.recommendation_reason}</div>}
                </div>
                <div style={{ display: 'flex', gap: isMobile ? 12 : 16, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                  <RiskBadge risk={alt.supply_chain_risk} />
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 20,
                    background: alt.shortage_status === 'None' || alt.shortage_status === 'Resolved' ? T.successBg : T.warningBg,
                    color: alt.shortage_status === 'None' || alt.shortage_status === 'Resolved' ? T.success : T.warning,
                    fontSize: 11, fontWeight: 700,
                  }}>
                    {alt.shortage_status === 'None' || alt.shortage_status === 'Resolved' ? 'Available' : alt.shortage_status}
                  </span>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{formatPrice(alt.nadac_per_unit)}</div>
                    <div style={{ fontSize: 9, color: T.textLight, textTransform: 'uppercase' }}>per unit</div>
                  </div>
                  <button onClick={() => onSelectProduct(alt.product_id)}
                    style={{ padding: '6px 12px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.offWhite, color: T.navy, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                    View Detail
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER: Active tab content
  // ============================================================
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'active-shortages': return renderActiveShortages();
      case 'risk-radar': return renderRiskRadar();
      case 'cause-analysis':
        return (
          <>
            {renderCauseSummaryStats()}
            {renderCauseFilters()}
            {renderCauseAnalysisFeed()}
          </>
        );
      case 'alternatives': return renderAlternativesTab();
      default: return renderActiveShortages();
    }
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  // Filter deduplicated shortages by country and shortage type
  const displayShortages = dedupedShortages.filter(s => {
    if (countryFilter && (s.country_of_origin || s.api_source_country || 'Unknown') !== countryFilter) return false;
    if (shortageTypeFilter !== 'All') {
      const availability = (s.availability || '').toLowerCase();
      if (shortageTypeFilter === 'Limited' && availability !== 'limited availability') return false;
      if (shortageTypeFilter === 'Unavailable' && availability !== 'unavailable') return false;
    }
    return true;
  });

  // Apply search filter for table
  const searchedShortages = productSearch.trim()
    ? displayShortages.filter(s => {
        const q = productSearch.toLowerCase();
        return (s.product_name || '').toLowerCase().includes(q)
          || (s.generic_name || '').toLowerCase().includes(q)
          || (s.ndc_code || '').toLowerCase().includes(q)
          || (s.manufacturer || '').toLowerCase().includes(q);
      })
    : displayShortages;

  // Reset table page when filters/search change
  React.useEffect(() => { setTablePage(1); }, [countryFilter, shortageTypeFilter, productSearch]);

  // Paginate the searched shortages
  const totalTablePages = Math.ceil(searchedShortages.length / tablePageSize);
  const paginatedShortages = searchedShortages.slice(
    (tablePage - 1) * tablePageSize,
    tablePage * tablePageSize,
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
      {renderHero()}
      {renderSummaryTiles()}

      {/* Countries detail section */}
      {activeDetailSection === 'countries' && renderCountriesDetail()}

      {/* Cause Analysis detail section */}
      {activeDetailSection === 'cause' && (
        <div style={{ marginBottom: 24 }}>
          {renderCauseSummaryStats()}
          {renderCauseFilters()}
          {renderCauseAnalysisFeed()}
        </div>
      )}

      {/* Risk Analysis detail section */}
      {activeDetailSection === 'risk' && (
        <div style={{ marginBottom: 24 }}>
          {renderRiskRadar()}
        </div>
      )}

      {/* Shortage Type Filter (always visible) */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Shortage Type</span>
        {(['All', 'Limited', 'Unavailable'] as const).map(type => {
          const active = shortageTypeFilter === type;
          return (
            <button
              key={type}
              onClick={() => setShortageTypeFilter(type)}
              style={{
                padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500,
                border: `1px solid ${active ? T.gold : T.border}`,
                background: active ? `${T.gold}15` : T.white,
                color: active ? T.navy : T.textMid,
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >{type}</button>
          );
        })}
      </div>

      {/* Active Shortages — Drugs-page table style */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, padding: isMobile ? 16 : 24,
        boxShadow: T.shadow,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 4 }}>Shortage Products</div>
        <div style={{ fontSize: 12, color: T.textLight, marginBottom: 16 }}>
          {(countryFilter || shortageTypeFilter !== 'All' ? displayShortages.length : (shortagesData?.shortage_product_count ?? dedupedShortages.length)).toLocaleString()} unique drugs in shortage{countryFilter ? ` (filtered: ${countryFilter})` : ''}
        </div>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Search by name, NDC, or manufacturer..."
            value={productSearch}
            onChange={e => setProductSearch(e.target.value)}
            style={{
              width: '100%', maxWidth: 360, padding: '10px 14px',
              borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
              fontSize: 13, color: T.textDark, outline: 'none', background: T.offWhite,
            }}
          />
        </div>

        {shortagesLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><IconSpinner /></div>
        ) : shortagesError ? (
          <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 32, textAlign: 'center' }}>
            <IconAlert />
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.danger, marginTop: 12 }}>Failed to Load Data</h3>
            <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>{shortagesError}</p>
            <button onClick={loadShortages} style={{ marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm, border: 'none', background: T.navy, color: T.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
          </div>
        ) : paginatedShortages.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.textLight, fontSize: 14 }}>
            {productSearch ? 'No products match your search.' : 'No active shortages matching your filters.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.offWhite }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Drug</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Manufacturer</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Severity</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Status</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Days Active</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Reason</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Reported</th>
                </tr>
              </thead>
              <tbody>
                {paginatedShortages.map((s, idx) => {
                  const sevColor = getSeverityColor(s.severity);
                  return (
                    <tr
                      key={s.shortage_id}
                      onClick={() => s.product_id ? onSelectProduct(s.product_id) : undefined}
                      style={{
                        cursor: s.product_id ? 'pointer' : 'default',
                        borderBottom: idx < paginatedShortages.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.white; }}
                    >
                      <td style={{ padding: '14px 16px', maxWidth: 280 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.product_name || 'Unknown'}
                        </div>
                        {s.generic_name && (
                          <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>{s.generic_name}</div>
                        )}
                        {s.ndc_code && (
                          <div style={{ fontSize: 11, color: T.textLight, fontFamily: 'monospace', marginTop: 1 }}>{s.ndc_code}</div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 13, color: T.textMid }}>{s.manufacturer || 'Unknown'}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                          background: sevColor.bg, color: sevColor.text, border: `1px solid ${sevColor.border}`,
                        }}>{s.severity}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 12, fontWeight: 600,
                          color: s.shortage_status === 'Unavailable' ? T.danger : T.warning,
                        }}>{s.shortage_status}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>
                          {s.days_active != null ? s.days_active : '—'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', maxWidth: 200 }}>
                        <span style={{ fontSize: 12, color: T.textMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {s.reason || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 12, color: T.textLight }}>
                          {s.reported_date
                            ? new Date(s.reported_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination controls */}
        {totalTablePages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 16, padding: '12px 0',
            borderTop: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: 12, color: T.textLight }}>
              Showing {((tablePage - 1) * tablePageSize) + 1}–{Math.min(tablePage * tablePageSize, searchedShortages.length)} of {searchedShortages.length.toLocaleString()} products
            </span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                disabled={tablePage <= 1}
                onClick={() => setTablePage(1)}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: tablePage <= 1 ? T.textLight : T.navy,
                  fontSize: 12, fontWeight: 600, cursor: tablePage <= 1 ? 'default' : 'pointer',
                  fontFamily: 'inherit', opacity: tablePage <= 1 ? 0.5 : 1,
                }}
              >First</button>
              <button
                disabled={tablePage <= 1}
                onClick={() => setTablePage(p => Math.max(1, p - 1))}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: tablePage <= 1 ? T.textLight : T.navy,
                  fontSize: 12, fontWeight: 600, cursor: tablePage <= 1 ? 'default' : 'pointer',
                  fontFamily: 'inherit', opacity: tablePage <= 1 ? 0.5 : 1,
                }}
              >Prev</button>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.textDark, padding: '0 8px' }}>
                Page {tablePage} of {totalTablePages}
              </span>
              <button
                disabled={tablePage >= totalTablePages}
                onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: tablePage >= totalTablePages ? T.textLight : T.navy,
                  fontSize: 12, fontWeight: 600, cursor: tablePage >= totalTablePages ? 'default' : 'pointer',
                  fontFamily: 'inherit', opacity: tablePage >= totalTablePages ? 0.5 : 1,
                }}
              >Next</button>
              <button
                disabled={tablePage >= totalTablePages}
                onClick={() => setTablePage(totalTablePages)}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: tablePage >= totalTablePages ? T.textLight : T.navy,
                  fontSize: 12, fontWeight: 600, cursor: tablePage >= totalTablePages ? 'default' : 'pointer',
                  fontFamily: 'inherit', opacity: tablePage >= totalTablePages ? 0.5 : 1,
                }}
              >Last</button>
            </div>
          </div>
        )}
      </div>

      {/* Alternatives panel */}
      {renderAlternativesPanel()}
    </div>
  );
}
